<template>
  <div class="container assessment-management-container">
    <el-card shadow="hover">

      <div v-if="viewMode === 'list'">
        <div class="card-header">
          <h2>إدارة التقييمات الأكاديمية</h2>
          <el-button type="primary" @click="showCreateView">
            <el-icon><Plus /></el-icon> إنشاء تقييم جديد
          </el-button>
        </div>
        <el-divider />

        <div class="filter-section">
            <el-select v-model="filters.study_year_id" placeholder="السنة الدراسية" clearable @change="handleFilterChange" style="width: 200px;">
                <el-option v-for="year in metaData.studyYears" :key="year.id" :label="year.name" :value="year.id" />
            </el-select>
            <el-select v-model="filters.term_id" placeholder="الفصل الدراسي" clearable @change="handleFilterChange" style="width: 200px;">
                <el-option v-for="term in metaData.terms" :key="term.id" :label="term.name" :value="term.id" />
            </el-select>
            <el-select v-model="filters.class_id" placeholder="الصف" clearable @change="handleFilterChange" style="width: 200px;">
                <el-option v-for="cls in metaData.classes" :key="cls.id" :label="cls.name" :value="cls.id" />
            </el-select>
        </div>

        <el-table :data="allAssessments" v-loading="loading" border style="width: 100%">
          <el-table-column prop="title" label="عنوان التقييم" min-width="200"/>
          <el-table-column prop="created_by_teacher.name" label="المعلم" width="180"/>
          <el-table-column prop="course_offering.subject.name" label="المادة" width="150"/>
          <el-table-column prop="course_offering.school_class.name" label="الصف" width="120"/>
           <el-table-column prop="assessment_type.name" label="النوع" width="120"/>
          <el-table-column prop="is_online_quiz" label="اختبار إلكتروني؟" width="120" align="center">
            <template #default="{ row }"><el-tag :type="row.is_online_quiz ? 'success' : 'info'">{{ row.is_online_quiz ? 'نعم' : 'لا' }}</el-tag></template>
          </el-table-column>
          <el-table-column label="حالة الرصد" width="160" align="center">
            <template #default="{ row }">
                <el-tooltip
                    class="box-item"
                    effect="dark"
                    :content="`تم رصد ${row.student_scores_count || 0} من أصل ${row.students_count || 0} طالب`"
                    placement="top"
                >
                    <el-progress 
                        :text-inside="true" 
                        :stroke-width="22" 
                        :percentage="row.students_count > 0 ? Math.round((row.student_scores_count / row.students_count) * 100) : 0" 
                        :status="row.students_count > 0 && row.student_scores_count === row.students_count ? 'success' : ''"
                    >
                      <span>{{ row.student_scores_count || 0 }} / {{ row.students_count || 0 }}</span>
                    </el-progress>
                </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="الإجراءات" width="250" fixed="right" align="center">
            <template #default="{ row }">
              <el-button size="small" type="primary" @click="openGradingView(row)" :icon="Edit">رصد الدرجات</el-button>
              <el-dropdown>
                <el-button size="small" type="primary" plain class="ml-2">
                  المزيد <el-icon class="el-icon--right"><ArrowDown /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <!-- ✨ --- بداية الإضافة: زر عرض التفاصيل الكاملة --- ✨ -->
                    <el-dropdown-item @click="showAssessmentDetails(row)" :icon="View">
                      عرض التفاصيل
                    </el-dropdown-item>
                    <!-- --- نهاية الإضافة --- ✨ -->
                    <el-dropdown-item @click="printGradesReport(row)" :icon="Printer">
                      طباعة التقرير
                    </el-dropdown-item>
                    <el-dropdown-item @click="exportGrades(row)" :icon="Download">
                      تصدير (Excel)
                    </el-dropdown-item>
                    <el-dropdown-item @click="openEditView(row)" disabled divided>
                      تعديل
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination v-if="pagination.total > 0" @current-change="handlePageChange" :current-page="pagination.current_page" :page-size="pagination.per_page" :total="pagination.total" layout="total, prev, pager, next" background />
        </div>
      </div>

      <div v-if="viewMode === 'create' || viewMode === 'edit'">
        <div class="card-header">
          <h2>{{ viewMode === 'create' ? 'إنشاء تقييم جديد' : 'تعديل التقييم' }}</h2>
          <el-button @click="showListView">الرجوع للقائمة</el-button>
        </div>
        <el-divider />

        <el-steps :active="activeStep" finish-status="success" simple style="margin-bottom: 30px;">
          <el-step title="اختيار المعلم والمقرر" :icon="CollectionTag" />
          <el-step title="تفاصيل التقييم" :icon="EditPen" />
          <el-step title="إضافة أسئلة (اختياري)" :icon="Finished" />
        </el-steps>

        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <div v-show="activeStep === 0">
              <el-alert title="الخطوة الأولى: تحديد المقرر" description="ابدأ باختيار المعلم، ثم سيتم عرض الفصول والشعب والمقررات المسندة إليه فقط." type="info" :closable="false" show-icon style="margin-bottom: 20px;" />
              <el-row :gutter="20">
                  <el-col :span="6">
                    <el-form-item label="اختر المعلم">
                      <el-select
                        v-model="courseFilters.teacher_id"
                        filterable
                        remote
                        reserve-keyword
                        placeholder="ابحث عن المعلم بالاسم..."
                        :remote-method="searchTeachers"
                        :loading="teacherSearchLoading"
                        @change="onTeacherChange"
                        style="width: 100%;"
                      >
                        <el-option
                          v-for="teacher in metaData.teachers"
                          :key="teacher.id"
                          :label="teacher.name"
                          :value="teacher.id"
                        >
                          <div class="teacher-option-with-button">
                            <span>{{ teacher.name }}</span>
                            <el-button
                              type="primary"
                              link
                              size="small"
                              @click.stop="showTeacherDetails(teacher)"
                            >
                              عرض التفاصيل
                            </el-button>
                          </div>
                        </el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
               
                  <el-col :span="6">
                    <el-form-item label="اختر الصف الدراسي">
                        <el-select v-model="courseFilters.class_id" placeholder="الصف" @change="onClassChange" style="width: 100%;" :disabled="!courseFilters.teacher_id">
                        <el-option v-for="cls in teacherClasses" :key="cls.id" :label="cls.name" :value="cls.id" />
                        </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="6">
                    <el-form-item label="اختر الشعبة">
                        <el-select v-model="courseFilters.section_id" placeholder="الشعبة" @change="onSectionChange" :disabled="!courseFilters.class_id" style="width: 100%;">
                        <el-option v-for="sec in filteredMeta.sections" :key="sec.id" :label="sec.name" :value="sec.id" />
                        </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="6">
                    <el-form-item label="اختر المادة الدراسية (المقرر)" prop="course_offering_id" >
                        <el-select v-model="form.course_offering_id" filterable placeholder="المقرر" style="width: 100%;" :disabled="!courseFilters.section_id">
                        <el-option v-for="course in filteredMeta.courses" :key="course.id" :label="course.subject.name" :value="course.id" />
                        </el-select>
                    </el-form-item>
                  </el-col>
              </el-row>
          </div>
          <div v-show="activeStep === 1">
              <el-alert title="الخطوة الثانية: تفاصيل التقييم" description="أدخل المعلومات الأساسية للتقييم مثل العنوان، النوع، والدرجة." type="info" :closable="false" show-icon style="margin-bottom: 20px;" />
              <el-form-item label="عنوان التقييم" prop="title">
                  <el-input v-model="form.title" placeholder="مثال: الامتحان النصفي للفصل الأول" />
              </el-form-item>
              <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="نوع التقييم" prop="assessment_type_id">
                        <el-select v-model="form.assessment_type_id" placeholder="اختر النوع" style="width: 100%;"><el-option v-for="type in assessmentTypes" :key="type.id" :label="type.name" :value="type.id" /></el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="الفصل الدراسي" prop="term_id">
                        <el-select v-model="form.term_id" placeholder="اختر الفصل الدراسي" style="width: 100%;"><el-option v-for="term in metaData.terms" :key="term.id" :label="term.name" :value="term.id" /></el-select>
                    </el-form-item>
                  </el-col>
              </el-row>
              <el-form-item label="الدرجة القصوى" prop="max_score">
                  <el-input-number v-model="form.max_score" :min="0" />
              </el-form-item>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="تاريخ النشر للطلاب (اختياري)">
                    <el-date-picker v-model="form.publish_date_time" type="datetime" style="width: 100%;" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="تاريخ التسليم النهائي (اختياري)">
                    <el-date-picker v-model="form.due_date_time" type="datetime" style="width: 100%;" />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="آخر موعد للرصد (اختياري)">
                    <el-date-picker v-model="form.grading_due_date_time" type="datetime" style="width: 100%;" />
                  </el-form-item>
                </el-col>
              </el-row>
               <el-form-item label="الوصف / التعليمات" prop="description">
                  <el-input v-model="form.description" type="textarea" :rows="3" />
              </el-form-item>
              <el-divider content-position="left">خيارات إضافية</el-divider>
              <el-form-item label="إعدادات الرؤية والنشر">
                  <el-checkbox v-model="form.is_visible_to_students" label="مرئي للطلاب" border />
                  <el-checkbox v-model="form.are_grades_published" label="الدرجات منشورة" border />
                  <el-checkbox v-model="form.is_online_quiz" label="اختبار إلكتروني (يحتوي على أسئلة)" border />
              </el-form-item>
          </div>
          <div v-show="activeStep === 2">
              <el-alert title="الخطوة الثالثة (اختياري): إضافة أسئلة" description="إذا كان هذا اختباراً إلكترونياً، يمكنك إضافة الأسئلة وخياراتها هنا." type="info" :closable="false" show-icon style="margin-bottom: 20px;" />
              <div v-for="(question, qIndex) in form.questions" :key="qIndex" class="question-card">
                  <el-card shadow="never">
                  <template #header>
                      <div class="card-header">
                      <span>السؤال {{ qIndex + 1 }}</span>
                      <el-button type="danger" :icon="Delete" circle @click="removeQuestion(qIndex)" />
                      </div>
                  </template>
                  <el-form-item :label="`نص السؤال`" :prop="`questions.${qIndex}.question_text`" :rules="{ required: true, message: 'نص السؤال مطلوب', trigger: 'blur' }">
                      <el-input v-model="question.question_text" type="textarea" :rows="2" />
                  </el-form-item>
                  <el-row :gutter="20">
                      <el-col :span="12">
                      <el-form-item :label="`الدرجة`" :prop="`questions.${qIndex}.points`" :rules="{ required: true, message: 'الدرجة مطلوبة', trigger: 'blur' }">
                          <el-input-number v-model="question.points" :min="0" />
                      </el-form-item>
                      </el-col>
                      <el-col :span="12">
                      <el-form-item label="نوع السؤال">
                          <el-select v-model="question.question_type" placeholder="اختر النوع" style="width: 100%;">
                          <el-option label="اختيار من متعدد (إجابة واحدة)" value="multiple_choice_single_answer"></el-option>
                          <el-option label="اختيار من متعدد (عدة إجابات)" value="multiple_choice_multiple_answers"></el-option>
                          <el-option label="صح / خطأ" value="true_false"></el-option>
                          <el-option label="إجابة قصيرة" value="short_answer"></el-option>
                          <el-option label="مقالي" value="essay"></el-option>
                          </el-select>
                      </el-form-item>
                      </el-col>
                  </el-row>
                  <el-divider content-position="left">الإجابات</el-divider>
                  <div v-if="question.question_type === 'multiple_choice_single_answer'">
                      <div v-for="(option, oIndex) in question.options" :key="oIndex" class="option-row">
                      <el-radio v-model="question.correct_answer_index" :label="oIndex" size="large">الإجابة الصحيحة</el-radio>
                      <el-input v-model="option.option_text" placeholder="نص الخيار" />
                      <el-button type="danger" :icon="Close" circle plain @click="removeOption(qIndex, oIndex)" />
                      </div>
                      <el-button type="primary" plain @click="addOption(qIndex)">إضافة خيار</el-button>
                  </div>
                  <div v-if="question.question_type === 'multiple_choice_multiple_answers'">
                      <div v-for="(option, oIndex) in question.options" :key="oIndex" class="option-row">
                      <el-checkbox v-model="option.is_correct_answer" size="large">إجابة صحيحة</el-checkbox>
                      <el-input v-model="option.option_text" placeholder="نص الخيار" />
                      <el-button type="danger" :icon="Close" circle plain @click="removeOption(qIndex, oIndex)" />
                      </div>
                      <el-button type="primary" plain @click="addOption(qIndex)">إضافة خيار</el-button>
                  </div>
                  <div v-if="question.question_type === 'true_false'">
                      <el-radio-group v-model="question.true_false_answer">
                          <el-radio :label="true" size="large" border>صح</el-radio>
                          <el-radio :label="false" size="large" border>خطأ</el-radio>
                      </el-radio-group>
                  </div>
                  <div v-if="question.question_type === 'short_answer' || question.question_type === 'essay'">
                      <el-input placeholder="سيقوم الطالب بكتابة الإجابة هنا" disabled />
                  </div>
                  </el-card>
              </div>
              <el-button @click="addQuestion" style="margin-top: 20px;" :icon="Plus">إضافة سؤال جديد</el-button>
          </div>
        </el-form>
        <div class="step-actions">
            <el-button @click="prevStep" v-if="activeStep > 0">السابق</el-button>
            <el-button type="primary" @click="nextStep" v-if="activeStep < 2">التالي</el-button>
            <el-button type="success" @click="submitAssessment" :loading="formLoading" v-if="(activeStep === 1 && !form.is_online_quiz) || activeStep === 2">
                إنهاء وحفظ التقييم
            </el-button>
        </div>
      </div>

      <div v-if="viewMode === 'grade'">
        <div class="card-header">
          <h2 v-if="gradingAssessment">رصد درجات لـ: {{ gradingAssessment.title }}</h2>
          <el-button @click="showListView">الرجوع للقائمة</el-button>
        </div>
        <el-divider />

        <div v-if="gradingAssessment && !gradingAssessment.is_online_quiz">
          <el-table :data="studentsToGrade" v-loading="isGradingLoading" border height="500px">
            <el-table-column prop="name" label="اسم الطالب" fixed width="250" />
            <el-table-column label="حالة التسليم" width="200">
              <template #default="{ row }">
                <el-select v-model="row.score_record.status" placeholder="الحالة">
                  <el-option label="لم يسلم بعد" value="Pending Submission"></el-option>
                  <el-option label="تم التسليم" value="Submitted"></el-option>
                  <el-option label="متأخر" value="Late Submission"></el-option>
                  <el-option label="تم الرصد" value="Graded"></el-option>
                  <el-option label="غائب" value="Missing"></el-option>
                  <el-option label="معذور" value="Excused"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="الدرجة">
              <template #default="{ row }">
                <el-input-number 
                  v-model="row.score_record.score_obtained" 
                  :min="0" 
                  :max="row.max_score"
                  controls-position="right"
                  style="width: 120px;"
                  @change="onScoreChange(row)"
                  :disabled="row.score_record.status === 'Pending Submission' || row.score_record.status === 'Missing'"
                />
                 / {{ row.max_score }}
              </template>
            </el-table-column>
          </el-table>
          <div class="step-actions" style="justify-content: flex-end; margin-top: 20px;">
            <el-button type="success" @click="submitDirectGrades" :loading="isSavingGrades">حفظ الدرجات</el-button>
          </div>
        </div>

        <div v-if="gradingAssessment && gradingAssessment.is_online_quiz">
           <el-table :data="studentSubmissions" v-loading="isGradingLoading" border>
                <el-table-column prop="student.name" label="اسم الطالب" />
                <el-table-column prop="submission.status" label="الحالة">
                    <template #default="{ row }">
                        <el-tag :type="row.submission.status === 'Graded' ? 'success' : (row.submission.status === 'Submitted' ? 'primary' : 'info')">{{ row.submission.status }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="submission.score_obtained" label="الدرجة النهائية" />
                <el-table-column label="الإجراء">
                    <template #default="{ row }">
                        <el-button v-if="row.submission.id" size="small" type="primary" @click="openSubmissionDetails(row.submission)">
                            عرض وتصحيح التسليم
                        </el-button>
                         <span v-else>لم يسلم بعد</span>
                    </template>
                </el-table-column>
           </el-table>
        </div>
      </div>

       <el-dialog v-model="isGradingDetailVisible" title="تصحيح تسليم الطالب" width="70%" top="5vh" fullscreen>
            <div v-if="gradingSubmissionDetails" v-loading="isDetailLoading">
                <div class="grading-header">
                    <h3>الطالب: {{ gradingSubmissionDetails.student.name }}</h3>
                    <h4>الدرجة المحتسبة تلقائياً: {{ autoCalculatedScore }} / {{ gradingAssessment.max_score }}</h4>
                </div>
                <div v-for="(question, qIndex) in gradingSubmissionDetails.assessment.quiz_questions" :key="question.id" class="question-review">
                    <p><strong>{{ qIndex + 1 }}. {{ question.question_text }} ({{ question.points }} درجات)</strong></p>
                    
                    <div v-if="isManuallyGraded(question.question_type)">
                    <p><strong>إجابة الطالب:</strong></p>
                    <el-input type="textarea" :model-value="getStudentAnswer(question.id).answer_text || 'لم تتم الإجابة'" disabled :rows="3" />
                    <el-form-item label="الدرجة الممنوحة" style="margin-top: 10px;">
                        <el-input-number v-model="getStudentAnswer(question.id).points_awarded" :min="0" :max="parseFloat(question.points)" @change="recalculateFinalScore" />
                    </el-form-item>
                    </div>
                    
                    <div v-else>
                    <p><strong>إجابة الطالب:</strong> {{ getStudentAnswerOptionText(question.id) }} 
                        <el-tag :type="isAnswerCorrect(question.id) ? 'success' : 'danger'" size="small">
                        {{ isAnswerCorrect(question.id) ? 'صحيحة' : 'خاطئة' }}
                        </el-tag>
                    </p>
                    <p><strong>الإجابة الصحيحة:</strong> {{ getCorrectOptionText(question) }}</p>
                    </div>
                    <el-divider />
                </div>
                
                <div class="final-score-section">
                    <el-form-item label="الدرجة النهائية (يمكن تعديلها)">
                    <el-input-number v-model="finalScore" :min="0" :max="gradingAssessment.max_score" />
                    </el-form-item>
                </div>
            </div>
            <template #footer>
                <el-button @click="isGradingDetailVisible = false">إلغاء</el-button>
                <el-button type="primary" @click="submitFinalGrade" :loading="isSavingGrades">حفظ التصحيح والدرجة النهائية</el-button>
            </template>
       </el-dialog>

    </el-card>

    <el-dialog v-model="teacherDetailsVisible" title="تفاصيل إسناد المعلم" width="500px">
        <div v-if="selectedTeacherForDetails">
            <h3>{{ selectedTeacherForDetails.name }}</h3>
            <el-divider />
            <div v-if="selectedTeacherForDetails.classes_taught" class="info-section">
                <span class="info-heading">الفصول المسندة:</span>
                <div class="tag-container">
                    <el-tag v-for="cls in selectedTeacherForDetails.classes_taught.split(', ')" :key="cls" class="info-tag" size="default" type="info">{{ cls }}</el-tag>
                </div>
            </div>
            <div v-if="selectedTeacherForDetails.subjects_taught" class="info-section">
                <span class="info-heading">المواد المسندة:</span>
                <div class="tag-container">
                    <el-tag v-for="sub in selectedTeacherForDetails.subjects_taught.split(', ')" :key="sub" class="info-tag" size="default">{{ sub }}</el-tag>
                </div>
            </div>
            <div v-if="!selectedTeacherForDetails.classes_taught && !selectedTeacherForDetails.subjects_taught">
                <p>لا توجد بيانات إسناد حالية لهذا المعلم.</p>
            </div>
        </div>
        <template #footer>
            <el-button @click="teacherDetailsVisible = false">إغلاق</el-button>
        </template>
    </el-dialog>

    <!-- ✨ --- بداية الإضافة: نافذة جديدة لعرض تفاصيل التقييم --- ✨ -->
    <el-dialog v-model="assessmentDetailsVisible" :title="`تفاصيل التقييم: ${selectedAssessmentForDetails ? selectedAssessmentForDetails.title : ''}`" width="600px">
        <div v-if="selectedAssessmentForDetails" class="details-dialog-content">
            <el-descriptions :column="2" border>
                <el-descriptions-item label="المعلم">{{ selectedAssessmentForDetails.created_by_teacher.name }}</el-descriptions-item>
                <el-descriptions-item label="المادة">{{ selectedAssessmentForDetails.course_offering?.subject?.name }}</el-descriptions-item>
                <el-descriptions-item label="الصف">{{ selectedAssessmentForDetails.course_offering?.school_class?.name }}</el-descriptions-item>
                <el-descriptions-item label="الشعبة">{{ selectedAssessmentForDetails.section?.name }}</el-descriptions-item>
                <el-descriptions-item label="الفصل الدراسي">{{ selectedAssessmentForDetails.term?.name }}</el-descriptions-item>
                <el-descriptions-item label="نوع التقييم">{{ selectedAssessmentForDetails.assessment_type?.name }}</el-descriptions-item>
                <el-descriptions-item label="الدرجة القصوى">{{ selectedAssessmentForDetails.max_score }}</el-descriptions-item>
                <el-descriptions-item label="تاريخ الإنشاء">{{ new Date(selectedAssessmentForDetails.created_at).toLocaleString() }}</el-descriptions-item>
            </el-descriptions>

            <el-divider content-position="left">الوصف والتعليمات</el-divider>
            <div class="description-box">
                <p>{{ selectedAssessmentForDetails.description || 'لا يوجد وصف.' }}</p>
            </div>
            
            <el-divider content-position="left">التواريخ والحالة</el-divider>
             <el-descriptions :column="2" border>
                <el-descriptions-item label="تاريخ النشر">{{ selectedAssessmentForDetails.publish_date_time ? new Date(selectedAssessmentForDetails.publish_date_time).toLocaleString() : 'غير محدد' }}</el-descriptions-item>
                <el-descriptions-item label="تاريخ التسليم">{{ selectedAssessmentForDetails.due_date_time ? new Date(selectedAssessmentForDetails.due_date_time).toLocaleString() : 'غير محدد' }}</el-descriptions-item>
                <el-descriptions-item label="آخر موعد للرصد">{{ selectedAssessmentForDetails.grading_due_date_time ? new Date(selectedAssessmentForDetails.grading_due_date_time).toLocaleString() : 'غير محدد' }}</el-descriptions-item>
                 <el-descriptions-item label="الحالة">
                    <div class="status-tags">
                        <el-tag :type="selectedAssessmentForDetails.is_online_quiz ? 'success' : 'info'">{{ selectedAssessmentForDetails.is_online_quiz ? 'اختبار إلكتروني' : 'تقييم عادي' }}</el-tag>
                        <el-tag :type="selectedAssessmentForDetails.is_visible_to_students ? 'success' : 'danger'">{{ selectedAssessmentForDetails.is_visible_to_students ? 'مرئي للطلاب' : 'مخفي' }}</el-tag>
                        <el-tag :type="selectedAssessmentForDetails.are_grades_published ? 'success' : 'warning'">{{ selectedAssessmentForDetails.are_grades_published ? 'الدرجات منشورة' : 'الدرجات غير منشورة' }}</el-tag>
                    </div>
                </el-descriptions-item>
            </el-descriptions>

        </div>
        <template #footer>
            <el-button @click="assessmentDetailsVisible = false">إغلاق</el-button>
        </template>
    </el-dialog>
    <!-- --- نهاية الإضافة --- ✨ -->

  </div>
</template>


<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import * as XLSX from 'xlsx';
import { 
  CollectionTag, EditPen, Finished, Plus, Delete, Close, Edit, 
  Printer, Download, ArrowDown, School, Collection, View 
} from '@element-plus/icons-vue';

export default {
  name: 'AssessmentManagement',
  components: { 
    CollectionTag, EditPen, Finished, Plus, Delete, Close, Edit, 
    Printer, Download, ArrowDown, School, Collection, View
  },
  data() {
    return {
      api: null,
      viewMode: 'list', // list, create, edit, grade
      gradingAssessment: null,
      studentsToGrade: [],
      studentSubmissions: [],
      isGradingLoading: false,
      isSavingGrades: false,
      isGradingDetailVisible: false,
      isDetailLoading: false,
      gradingSubmissionDetails: null,
      finalScore: 0,
      filteredMeta: {
        sections: [],
        courses: []
      },
      teacherClasses: [],
      
      // List View State
      allAssessments: [],
      loading: false,
      pagination: { current_page: 1, per_page: 15, total: 0 },
      filters: { study_year_id: null, class_id: null, term_id: null },
      metaData: { studyYears: [], classes: [], terms: [], teachers: [] },
      
      // Create/Edit View State
      activeStep: 0,
      courseFilters: { class_id: null, section_id: null },
      teacherSearchLoading: false, 
      form: this.getInitialForm(),
      rules: {
        course_offering_id: [{ required: true, message: 'المقرر الدراسي مطلوب', trigger: 'change' }],
        title: [{ required: true, message: 'عنوان التقييم مطلوب', trigger: 'blur' }],
        assessment_type_id: [{ required: true, message: 'نوع التقييم مطلوب', trigger: 'change' }],
        term_id: [{ required: true, message: 'الفصل الدراسي مطلوب', trigger: 'change' }],
        max_score: [{ required: true, message: 'الدرجة القصوى مطلوبة', trigger: 'blur' }],
        created_by_teacher_id: [{ required: true, message: 'المعلم مطلوب', trigger: 'change' }],
      },
      assessmentTypes: [],
      formLoading: false,

      teacherDetailsVisible: false,
      selectedTeacherForDetails: null,

      // ✨ --- بداية الإضافة: متغيرات حالة نافذة تفاصيل التقييم --- ✨
      assessmentDetailsVisible: false,
      selectedAssessmentForDetails: null,
      // --- نهاية الإضافة --- ✨
    };
  },
  computed: {
    autoCalculatedScore() {
      if (!this.gradingSubmissionDetails || !this.gradingSubmissionDetails.quiz_answers) return 0;
      return this.gradingSubmissionDetails.quiz_answers.reduce((total, answer) => {
        return total + (parseFloat(answer.points_awarded) || 0);
      }, 0);
    },
    isNextDisabled() {
      if (this.activeStep === 0) return !this.form.course_offering_id;
      return false;
    }
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use(config => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchAssessments();
    this.fetchMetaDataForFilters();
  },
  methods: {
    showTeacherDetails(teacher) {
      this.selectedTeacherForDetails = teacher;
      this.teacherDetailsVisible = true;
    },

    // ✨ --- بداية الإضافة: دالة جديدة لفتح نافذة تفاصيل التقييم --- ✨
    showAssessmentDetails(assessment) {
      this.selectedAssessmentForDetails = assessment;
      this.assessmentDetailsVisible = true;
    },
    // --- نهاية الإضافة --- ✨

    onScoreChange(studentRow) {
      if (studentRow.score_record.score_obtained !== null && studentRow.score_record.score_obtained >= 0) {
        studentRow.score_record.status = 'Graded';
      }
    },
    
    // --- View Control Methods ---
    showListView() { this.viewMode = 'list'; this.fetchAssessments(); },
    showCreateView() {
      this.form = this.getInitialForm();
      this.activeStep = 0;
      this.courseFilters = { teacher_id: null, class_id: null, section_id: null };
      this.filteredMeta = { classes: [], sections: [], courses: [] };
      this.viewMode = 'create';
      this.fetchAllTeachers(); 
    },
    openEditView(assessment) {
        ElMessage.info('ميزة التعديل قيد الإنشاء.');
    },
    async openGradingView(assessment) {
        this.gradingAssessment = assessment;
        this.viewMode = 'grade';
        this.isGradingLoading = true;
        try {
            if (assessment.is_online_quiz) {
                const response = await this.api.get(`/assessments/${assessment.id}/submissions`);
                this.studentSubmissions = response.data;
            } else {
                const response = await this.api.get(`/assessments/${assessment.id}/grading-sheet`);
                this.studentsToGrade = response.data;
            }
        } catch (error) {
            ElMessage.error('فشل في جلب بيانات الرصد.');
            this.showListView();
        } finally {
            this.isGradingLoading = false;
        }
    },
    async openSubmissionDetails(submission) {
        this.isGradingDetailVisible = true;
        this.isDetailLoading = true;
        this.gradingSubmissionDetails = null;
        try {
            const response = await this.api.get(`/submissions/${submission.id}/details`);
            this.gradingSubmissionDetails = response.data;
            this.finalScore = this.gradingSubmissionDetails.score_obtained || this.autoCalculatedScore;
        } catch (error) {
            ElMessage.error('فشل في جلب تفاصيل التسليم.');
            this.isGradingDetailVisible = false;
        } finally {
            this.isDetailLoading = false;
        }
    },

    // --- Helper methods for grading details view ---
    getStudentAnswer(questionId) {
        if (!this.gradingSubmissionDetails) return { points_awarded: 0 };
        return this.gradingSubmissionDetails.quiz_answers.find(a => a.quiz_question_id === questionId) || { points_awarded: 0 };
    },
    getStudentAnswerText(questionId) {
        return this.getStudentAnswer(questionId)?.answer_text || 'لم تتم الإجابة';
    },
    isAnswerCorrect(questionId) {
        return this.getStudentAnswer(questionId)?.is_marked_correct || false;
    },
    getStudentAnswerOptionText(questionId) {
        const answer = this.getStudentAnswer(questionId);
        return answer?.chosen_option?.option_text || 'لم يختر إجابة';
    },
    getCorrectOptionText(question) {
        const correctOption = question.options.find(o => o.is_correct_answer);
        return correctOption ? correctOption.option_text : 'N/A';
    },
    isManuallyGraded(type) {
        return ['short_answer', 'essay'].includes(type);
    },
    recalculateFinalScore() {
        this.finalScore = this.autoCalculatedScore;
    },
    async submitFinalGrade() {
        this.isSavingGrades = true;
        try {
            const manuallyGradedAnswers = this.gradingSubmissionDetails.assessment.quiz_questions
                .filter(q => this.isManuallyGraded(q.question_type))
                .map(q => {
                    const answer = this.getStudentAnswer(q.id);
                    return { id: answer.id, points_awarded: answer.points_awarded };
                });
            
            const payload = {
                answers: manuallyGradedAnswers,
                final_score: this.finalScore,
            };

            await this.api.post(`/submissions/${this.gradingSubmissionDetails.id}/grade`, payload);
            ElMessage.success('تم حفظ التصحيح بنجاح!');
            this.isGradingDetailVisible = false;
            this.openGradingView(this.gradingAssessment); 
        } catch (error) {
            ElMessage.error('فشل في حفظ التصحيح.');
        } finally {
            this.isSavingGrades = false;
        }
    },

    // --- Grading Methods ---
    async submitDirectGrades() {
      this.isSavingGrades = true;
      try {
          const payload = {
              scores: this.studentsToGrade.map(student => ({
                  student_id: student.id,
                  score_obtained: student.score_record.score_obtained,
                  status: student.score_record.status,
              }))
          };
          await this.api.post(`/assessments/${this.gradingAssessment.id}/scores`, payload);
          ElMessage.success('تم حفظ الدرجات بنجاح!');
          this.showListView();
      } catch (error) { ElMessage.error('فشل في حفظ الدرجات.'); } 
      finally { this.isSavingGrades = false; }
    },
    
    // --- Data Fetching Methods ---
    async fetchMetaDataForFilters() {
      try {
        this.fetchAllTeachers(); 
        const [classesRes, typesRes, termsRes, yearsRes] = await Promise.all([
          this.api.get('/classes'),
          this.api.get('/assessment-types'),
          this.api.get('/terms'),
          this.api.get('/study-years'),
        ]);
        this.metaData.classes = classesRes.data;
        this.assessmentTypes = typesRes.data;
        this.metaData.terms = termsRes.data.data || termsRes.data;
        this.metaData.studyYears = yearsRes.data.data || yearsRes.data;
      } catch (error) { ElMessage.error("فشل في جلب البيانات الأساسية للفلترة."); }
    },
    async fetchAssessments() {
      this.loading = true;
      try {
        const params = { page: this.pagination.current_page, per_page: this.pagination.per_page, ...this.filters };
        const response = await this.api.get('/assessments', { params });
        this.allAssessments = response.data.data;
        this.pagination.total = response.data.total;
        this.pagination.current_page = response.data.current_page;
      } catch (error) {
        ElMessage.error('فشل في جلب التقييمات.');
      } finally {
        this.loading = false;
      }
    },
    handleFilterChange() { this.pagination.current_page = 1; this.fetchAssessments(); },
    handlePageChange(newPage) { this.pagination.current_page = newPage; this.fetchAssessments(); },

    // --- Create/Edit Wizard Methods ---
    getInitialForm() { 
      return { 
        course_offering_id: null, section_id: null, title: '', 
        assessment_type_id: null, term_id: null, max_score: 100, 
        description: '', questions: [],
        created_by_teacher_id: null, publish_date_time: null,
        due_date_time: null, grading_due_date_time: null,
        is_online_quiz: false, is_visible_to_students: false,
        are_grades_published: false,
      }; 
    },
    async searchTeachers(query) {
      if (query) {
        this.teacherSearchLoading = true;
        try {
          const response = await this.api.get(`/teachers`, { params: { search: query } });
          this.metaData.teachers = response.data;
        } catch (error) {
          console.error("Error searching teachers:", error);
          ElMessage.error('فشل البحث عن المعلمين.');
        } finally {
          this.teacherSearchLoading = false;
        }
      } else {
        this.fetchAllTeachers();
      }
    },
    async fetchAllTeachers() {
        this.teacherSearchLoading = true;
        try {
            const response = await this.api.get(`/teachers`);
            this.metaData.teachers = response.data;
        } catch (error) {
             ElMessage.error('فشل في جلب قائمة المعلمين.');
        } finally {
            this.teacherSearchLoading = false;
        }
    },
    async onTeacherChange() {
      this.courseFilters.class_id = null;
      this.onClassChange();
      if (!this.courseFilters.teacher_id) {
        this.teacherClasses = [];
        return;
      }
      this.form.created_by_teacher_id = this.courseFilters.teacher_id;
      try {
        const response = await this.api.get(`/teachers/${this.courseFilters.teacher_id}/classes`);
        this.teacherClasses = response.data;
      } catch (error) { 
        ElMessage.error('فشل في جلب فصول المعلم.'); 
        this.teacherClasses = [];
      }
    },
    async onClassChange() {
      this.courseFilters.section_id = null; this.filteredMeta.sections = [];
      this.onSectionChange();
      if (!this.courseFilters.teacher_id || !this.courseFilters.class_id) return;
      try {
        const response = await this.api.get(`/teachers/${this.courseFilters.teacher_id}/classes/${this.courseFilters.class_id}/sections`);
        this.filteredMeta.sections = response.data;
      } catch (error) { ElMessage.error('فشل في جلب شعب المعلم.'); }
    },
    async onSectionChange() {
      this.form.course_offering_id = null; this.filteredMeta.courses = [];
      if (!this.courseFilters.teacher_id || !this.courseFilters.section_id) return;
      this.form.section_id = this.courseFilters.section_id;
      try {
        const response = await this.api.get(`/teachers/${this.courseFilters.teacher_id}/sections/${this.courseFilters.section_id}/course-offerings`);
        this.filteredMeta.courses = response.data;
      } catch (error) { ElMessage.error('فشل في جلب مقررات المعلم.'); }
    },
    nextStep() {
      if (this.activeStep === 0) {
        this.$refs.formRef.validateField(['course_offering_id', 'created_by_teacher_id'], (isValid) => {
          if (isValid) { this.activeStep++; } else { ElMessage.error('يرجى اختيار المعلم والمقرر الدراسي للمتابعة.'); }
        });
      } else if (this.activeStep === 1) {
        const fieldsToValidate = ['title', 'assessment_type_id', 'term_id', 'max_score'];
        this.$refs.formRef.validateField(fieldsToValidate, (isValid) => {
          if (isValid) {
            if (this.form.is_online_quiz) { this.activeStep++; } 
            else { ElMessage.info('هذه هي الخطوة النهائية. اضغط على "إنهاء وحفظ".'); }
          } else { ElMessage.error('يرجى ملء جميع حقول تفاصيل التقييم للمتابعة.'); }
        });
      }
    },
    prevStep() {
      if (this.activeStep > 0) this.activeStep--;
    },
    addQuestion() {
      this.form.questions.push({
        question_text: '', points: 1, question_type: 'multiple_choice_single_answer',
        correct_answer_index: 0, true_false_answer: true,
        options: [{ option_text: '', is_correct_answer: true }, { option_text: '', is_correct_answer: false }]
      });
    },
    removeQuestion(index) { this.form.questions.splice(index, 1); },
    addOption(qIndex) { this.form.questions[qIndex].options.push({ option_text: '', is_correct_answer: false }); },
    removeOption(qIndex, oIndex) { this.form.questions[qIndex].options.splice(oIndex, 1); },
    async submitAssessment() {
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        try {
          const assessmentResponse = await this.api.post('/assessments', this.form);
          const assessmentId = assessmentResponse.data.assessment.id;
          if (this.form.is_online_quiz && this.form.questions.length > 0) {
            const cleanQuestionsPayload = this.form.questions.map(q => {
              const questionData = {
                question_text: q.question_text,
                points: q.points,
                question_type: q.question_type,
              };

              if (q.question_type === 'multiple_choice_single_answer') {
                questionData.options = q.options.map((opt, index) => ({
                  option_text: opt.option_text,
                  is_correct_answer: index === q.correct_answer_index,
                }));
              } 
              else if (q.question_type === 'multiple_choice_multiple_answers') {
                questionData.options = q.options;
              }
              else if (q.question_type === 'true_false') {
                questionData.true_false_answer = q.true_false_answer;
              }
              
              return questionData;
            });
            await this.api.post(`/assessments/${assessmentId}/questions`, { questions: cleanQuestionsPayload });
          }
          ElMessage.success('تم حفظ التقييم بنجاح!');
          this.showListView();
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors).flat().join('<br/>') : (error.response?.data?.message || 'فشل في حفظ التقييم.');
          ElMessage.error({ message: errorMsg, dangerouslyUseHTMLString: true });
        } finally {
          this.formLoading = false;
        }
      });
    },
    async exportGrades(assessment) {
        ElMessage.info('جاري تجهيز التقرير...');
        try {
            const response = await this.api.get(`/assessments/${assessment.id}/grading-sheet`);
            const gradingSheet = response.data;

            if (!gradingSheet || gradingSheet.length === 0) {
                ElMessage.warning('لا يوجد طلاب في هذه الشعبة لتصدير درجاتهم.');
                return;
            }

            const reportHeader = [
                ["عنوان التقييم:", assessment.title],
                ["المعلم:", assessment.created_by_teacher.name],
                ["المادة:", assessment.course_offering?.subject?.name || 'N/A'],
                ["الفصل / الشعبة:", `${assessment.course_offering?.school_class?.name || ''} / ${assessment.section?.name || ''}`],
                ["نوع التقييم:", assessment.assessment_type?.name || 'N/A'],
                ["الدرجة القصوى:", assessment.max_score],
                ["تاريخ الإنشاء:", new Date(assessment.created_at).toLocaleDateString()],
                ["تسليم إلكتروني؟:", assessment.is_online_quiz ? 'نعم' : 'لا'],
                ["مرئي للطلاب؟:", assessment.is_visible_to_students ? 'نعم' : 'لا'],
                [], 
            ];

            const studentData = gradingSheet.map(student => {
                const scoreRecord = student.score_record || {};
                return {
                    "رقم القيد": student.srn || 'N/A',
                    "اسم الطالب": student.name || 'N/A',
                    "حالة التسليم": scoreRecord.status || 'N/A',
                    "الدرجة المرصودة": scoreRecord.score_obtained !== null && scoreRecord.score_obtained !== undefined 
                        ? scoreRecord.score_obtained 
                        : 'لم ترصد',
                    "تاريخ الرصد": scoreRecord.grading_timestamp ? new Date(scoreRecord.grading_timestamp).toLocaleString() : '-',
                };
            });

            const ws = XLSX.utils.aoa_to_sheet(reportHeader);
            XLSX.utils.sheet_add_json(ws, studentData, { origin: "A11" });

            ws['!cols'] = [ { wch: 15 }, { wch: 35 }, { wch: 20 }, { wch: 15 }, { wch: 25 } ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Grades Report');
            
            const fileName = `Grades_${assessment.title.replace(/\s+/g, '_')}.xlsx`;
            XLSX.writeFile(wb, fileName);

        } catch (error) {
            console.error("Export Error:", error);
            ElMessage.error('فشل في تصدير التقرير.');
        }
    },
    async printGradesReport(assessment) {
        ElMessage.info('جاري تجهيز التقرير للطباعة...');
        try {
            const response = await this.api.get(`/assessments/${assessment.id}/grading-sheet`);
            const gradingSheet = response.data;

            if (!gradingSheet || gradingSheet.length === 0) {
                return ElMessage.warning('لا يوجد طلاب لطباعة درجاتهم.');
            }

            const logoUrl = `${window.location.origin}/logo-school-one.png`;

            const reportHeaderHtml = `
                <div class="report-header">
                    <img src="${logoUrl}" alt="School Logo" />
                    <h1>كشف درجات التقييم</h1>
                    <h2>${assessment.title}</h2>
                </div>
                <table class="details-table">
                    <tr><td><strong>المعلم:</strong></td><td>${assessment.created_by_teacher.name}</td></tr>
                    <tr><td><strong>المادة:</strong></td><td>${assessment.course_offering.subject.name}</td></tr>
                    <tr><td><strong>الفصل / الشعبة:</strong></td><td>${assessment.course_offering.school_class.name} / ${assessment.section.name}</td></tr>
                    <tr><td><strong>نوع التقييم:</strong></td><td>${assessment.assessment_type.name}</td></tr>
                    <tr><td><strong>الدرجة القصوى:</strong></td><td>${assessment.max_score}</td></tr>
                </table>
            `;

            const tableHeaders = `
                <tr>
                    <th>رقم القيد</th>
                    <th>اسم الطالب</th>
                    <th>حالة التسليم</th>
                    <th>الدرجة المرصودة</th>
                    <th>تاريخ الرصد</th>
                </tr>
            `;
            const tableRows = gradingSheet.map(student => {
                const scoreRecord = student.score_record || {};
                const score = scoreRecord.score_obtained !== null && scoreRecord.score_obtained !== undefined 
                    ? scoreRecord.score_obtained 
                    : 'لم ترصد';
                const gradingDate = scoreRecord.grading_timestamp ? new Date(scoreRecord.grading_timestamp).toLocaleString('ar-LY') : '-';
                return `
                    <tr>
                        <td>${student.srn || 'N/A'}</td>
                        <td class="student-name">${student.name || 'N/A'}</td>
                        <td>${scoreRecord.status || 'N/A'}</td>
                        <td>${score}</td>
                        <td>${gradingDate}</td>
                    </tr>
                `;
            }).join('');

            const printContent = `
                <html>
                    <head>
                        <title>Grades Report - ${assessment.title}</title>
                        <style>
                            @import url('[https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700&display=swap](https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700&display=swap)');
                            body { font-family: 'Tajawal', sans-serif; direction: rtl; }
                            .report-container { width: 95%; margin: auto; }
                            .report-header { text-align: center; margin-bottom: 20px; }
                            .report-header img { max-width: 120px; }
                            .report-header h1 { margin: 5px 0; color: #002d55; }
                            .details-table { width: 100%; margin-bottom: 20px; border: 1px solid #eee; border-collapse: collapse; }
                            .details-table td { padding: 8px; border: 1px solid #eee; }
                            .main-table { width: 100%; border-collapse: collapse; font-size: 12px; }
                            .main-table th, .main-table td { border: 1px solid #ccc; padding: 8px; text-align: center; }
                            .main-table th { 
                                background-color: #002d55 !important; 
                                color: white !important; 
                                -webkit-print-color-adjust: exact; 
                                print-color-adjust: exact;
                            }
                            .main-table tr:nth-child(even) { background-color: #f9f9f9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                            .main-table td.student-name { text-align: right; }
                        </style>
                    </head>
                    <body>
                        <div class="report-container">
                            ${reportHeaderHtml}
                            <table class="main-table">
                                <thead>${tableHeaders}</thead>
                                <tbody>${tableRows}</tbody>
                            </table>
                        </div>
                    </body>
                </html>
            `;

            const printWindow = window.open('', '_blank');
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);

        } catch (error) {
            console.error("Print Error:", error);
            ElMessage.error('فشل في تجهيز التقرير للطباعة.');
        }
    }
  },
};
</script>

<style scoped>
.container {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.filter-section {
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}
.step-actions {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
}
.question-card {
    margin-bottom: 20px;
}
.option-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}
.grading-header, .final-score-section {
    padding: 15px;
    background-color: #f5f7fa;
    border-radius: 8px;
    margin-bottom: 20px;
}
.question-review {
    margin-bottom: 20px;
}
.ml-2 {
  margin-left: 8px;
}
/* ✨ --- بداية التعديل: تنسيق جديد ومحسّن لقائمة المعلمين --- ✨ */
.teacher-option-layout {
  padding: 8px 0;
  line-height: 1.6;
}

.teacher-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.info-section {
  margin-top: 8px;
}

.info-heading {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
  display: block;
}

.tag-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.info-tag {
  margin: 0; /* Override default margin */
}

/* ✨ --- بداية الإضافة: تنسيق لزر عرض التفاصيل --- ✨ */
.teacher-option-with-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
/* --- نهاية الإضافة --- ✨ */
/* ✨ --- بداية الإضافة: تنسيق لنافذة تفاصيل التقييم --- ✨ */
.details-dialog-content .el-descriptions {
    margin-bottom: 20px;
}
.description-box {
    background-color: #f5f7fa;
    padding: 15px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
    min-height: 80px;
}
.description-box p {
    margin: 0;
    white-space: pre-wrap; /* To respect new lines in description */
}
.status-tags {
    display: flex;
    gap: 8px;
}
/* --- نهاية الإضافة --- ✨ */
</style>

